/**
 * Descriptions are stored as Markdown. Anywhere we need a bare sentence —
 * meta tags, emails, calendar exports — we strip the marks rather than ship
 * asterisks and image syntax to the reader.
 */
export function markdownToPlainText(input: string | null | undefined): string {
  if (!input) return "";
  return (
    input
      // images: drop entirely (alt text rarely reads well in a snippet)
      .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
      // links: keep the label
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      // fenced/inline code fences
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/`([^`]*)`/g, "$1")
      // headings, quotes, list bullets at line start
      .replace(/^\s{0,3}(#{1,6}|>)\s*/gm, "")
      .replace(/^\s{0,3}([-*+]|\d+\.)\s+/gm, "")
      // emphasis marks
      .replace(/(\*\*|__)(.*?)\1/g, "$2")
      .replace(/(\*|_)(.*?)\1/g, "$2")
      // horizontal rules
      .replace(/^\s*([-*_]\s*){3,}$/gm, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/** Meta-description helper: plain text, trimmed to a sane length. */
export function markdownSnippet(input: string | null | undefined, max = 155): string {
  const text = markdownToPlainText(input);
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * Listing preview: plain text cut at a word boundary, plus a flag so the
 * caller knows whether to offer a "Read more" link.
 */
export function markdownPreview(
  input: string | null | undefined,
  max = 180,
): { text: string; truncated: boolean } {
  const text = markdownToPlainText(input);
  if (text.length <= max) return { text, truncated: false };
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const body = (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:—-]+$/, "");
  return { text: `${body}…`, truncated: true };
}
